import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { api } from '../lib/api';
import { EXPLORER_URL } from '../lib/network';
import { formatToken } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';
import { LAST_SLOT, SLOT_HALF, SLOT_QUARTER, SUB_SLOTS, slotLabel, slotTitle } from '../lib/slots';

/*
 * Slot test tool (testnet only; owner request 2026-10-01). Reaching slot 16 with real joins needs a huge tree, so the
 * operator can (1) activate an ID's slots up to any slot and (2) deliver fully funded seats into any active slot. Every
 * seat is paid in mock BTCB into the vault first and then follows the normal position table, routing, pools, recycle
 * and Leaders Club logic - so slot 16 (recycle table from cycle 1, no slot 17) can be tested in minutes.
 * Era sub-slots (rule 16 Section I): slot keys 50 = Slot ½ and 25 = Slot ¼ come before Slot 1 (¼ -> ½ -> 1 -> ... -> 16);
 * a seat into ½ / ¼ of an ID that entered higher opens its virtual sub-slot (rule 435).
 */
interface ActivateResult { bdId: string; entrySlot?: number; upToSlot?: number; highestSlotBefore: number; highestSlotAfter: number; txHash: string }
interface SeatResult { ownerBdId: string; slot: number; occupantBdId: string; seatValueAtomic: string; mintedAtomic: string; seats: Array<{ cycle: number; position: number; txHash: string }>; nextCycle: number; nextPosition: number; nextSlotOpened: boolean; virtual?: boolean; virtualOpened?: boolean }
/** The part of /api/public/onchain/params this tool reads (the Matrix's live slotValue figures). */
interface MatrixValues { slotOneValueAtomic: string; slotValuesAtomic?: string[]; subSlotValuesAtomic?: { half: string; quarter: string } | null }
const tx = (hash: string) => <a href={`${EXPLORER_URL}/tx/${hash}`} target="_blank" rel="noreferrer" className="tx-link">{hash.slice(0, 10)}…</a>;

const FULL_SLOTS = Array.from({ length: LAST_SLOT }, (_, index) => index + 1);
/** Seat pickers: ¼, ½, 1..16 (progression order). */
const SEAT_SLOTS = [...SUB_SLOTS, ...FULL_SLOTS];
/** Activation targets: ½ (Quarter-era IDs), 1 (Half / Quarter-era IDs), 2..16 - ¼ is the lowest slot, never a target. */
const ACTIVATION_SLOTS = [SLOT_HALF, ...FULL_SLOTS];
/** The deployed default Slot-1 value (0.0005 BTCB), used only until the on-chain params load. */
const DEFAULT_SLOT_ONE_ATOMIC = 500_000_000_000_000n;

const atomic = (value: string | undefined | null): bigint | null => (value && /^\d+$/.test(value) ? BigInt(value) : null);
/** The contract's slotValue(slot): Slot n = Slot-1 value x 2^(n-1), ½ = Slot-1 / 2, ¼ = Slot-1 / 4. */
function seatValueAtomic(slot: number, matrix: MatrixValues | null): bigint {
  const slotOne = atomic(matrix?.slotOneValueAtomic) ?? DEFAULT_SLOT_ONE_ATOMIC;
  if (slot === SLOT_HALF) return atomic(matrix?.subSlotValuesAtomic?.half) ?? slotOne >> 1n;
  if (slot === SLOT_QUARTER) return atomic(matrix?.subSlotValuesAtomic?.quarter) ?? slotOne >> 2n;
  return atomic(matrix?.slotValuesAtomic?.[slot - 1]) ?? slotOne << BigInt(slot - 1);
}
const btcb = (value: bigint) => formatToken(atomicToDecimal(value.toString()));
const activationHint = (slot: number) => (slot === SLOT_HALF ? ' · Quarter-era IDs' : slot === 1 ? ' · Half / Quarter-era IDs' : '');

export function AdminSlotTestTool({ slug, onChanged }: { slug: string; onChanged?: () => void }) {
  const [bdId, setBdId] = useState('');
  const [upToSlot, setUpToSlot] = useState('16');
  const [ownerBdId, setOwnerBdId] = useState('');
  const [slot, setSlot] = useState('16');
  const [occupantBdId, setOccupantBdId] = useState('');
  const [count, setCount] = useState('1');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activated, setActivated] = useState<ActivateResult | null>(null);
  const [seated, setSeated] = useState<SeatResult | null>(null);
  const [matrix, setMatrix] = useState<MatrixValues | null>(null);
  const valid = (value: string) => /^BD-\d{1,12}$/i.test(value.trim());

  // Seat values come from the live Matrix parameters; until they load (or if they cannot) the default Slot-1 value is used.
  useEffect(() => {
    let alive = true;
    api<{ matrix?: MatrixValues | null }>('/api/public/onchain/params')
      .then((params) => { if (alive && params?.matrix) setMatrix(params.matrix); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);
  const valueSource = matrix ? 'on-chain' : 'default Slot-1 value 0.0005 - on-chain params not loaded';
  const seatCount = Number(count);
  const seatValue = seatValueAtomic(Number(slot), matrix);

  const [progress, setProgress] = useState<string | null>(null);
  // Each seat is 3 transactions (~10-15 s); the site proxy cuts a request at 60 s, so seats are sent one per request.
  async function deliverSeats() {
    const total = Number(count);
    const body = { ownerBdId: ownerBdId.trim().toUpperCase(), slot: Number(slot), occupantBdId: occupantBdId.trim().toUpperCase(), count: 1 };
    setBusy('Seat delivery'); setError(null); setSeated(null);
    let merged: SeatResult | null = null;
    try {
      for (let index = 0; index < total; index += 1) {
        setProgress(`Seat ${index + 1} of ${total}…`);
        const result = await api<SeatResult>(`/api/${slug}/test-slot/seat`, { method: 'POST', body: JSON.stringify(body) });
        merged = merged ? { ...result, mintedAtomic: (BigInt(merged.mintedAtomic) + BigInt(result.mintedAtomic)).toString(), seats: [...merged.seats, ...result.seats], virtualOpened: merged.virtualOpened === true || result.virtualOpened === true } : result;
        setSeated(merged);
      }
      onChanged?.();
    } catch (cause) {
      setError(`${merged ? `${merged.seats.length} of ${total} seat(s) delivered, then: ` : ''}${cause instanceof Error ? cause.message : 'Seat delivery failed.'}`);
    } finally {
      setBusy(null); setProgress(null);
    }
  }

  async function run<T>(label: string, path: string, body: unknown, done: (result: T) => void) {
    setBusy(label); setError(null);
    try {
      done(await api<T>(`/api/${slug}/${path}`, { method: 'POST', body: JSON.stringify(body) }));
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : `${label} failed.`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel audit-table neon-panel" id="admin-slot-test">
      <div className="panel-heading">
        <div><span className="eyebrow">TESTING TOOL · BSC TESTNET ONLY</span><h2><FlaskConical size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Slot test tool (reach slot 16 without a huge tree)</h2></div>
      </div>
      <p className="empty-copy">
        Step 1 puts an existing ID directly into higher slots, walking from its entry slot (Standard IDs Slot 1, Half-era IDs Slot ½, Quarter-era IDs Slot ¼) along ¼ → ½ → 1 → … → 16 (it also enters Club 4 / 8 / 12 / 16 on the way). Step 2 delivers real, fully paid seats into that ID's matrix in the chosen slot: each seat's value is paid in mock BTCB into the vault and then follows the normal rules (owner payout, Sponsor-1 / Sponsor-2 / downline routing, Token Liquidity, Leaders Club, recycle).
        Slot 16 uses the recycle table from cycle 1 and never opens slot 17. Seat values ({valueSource}): ¼ = {btcb(seatValueAtomic(SLOT_QUARTER, matrix))}, ½ = {btcb(seatValueAtomic(SLOT_HALF, matrix))}, slot 1 = {btcb(seatValueAtomic(1, matrix))}, slot 4 = {btcb(seatValueAtomic(4, matrix))}, slot 8 = {btcb(seatValueAtomic(8, matrix))}, slot 12 = {btcb(seatValueAtomic(12, matrix))}, slot 16 = {btcb(seatValueAtomic(16, matrix))}. No wallet connection is needed; each seat sends 3 transactions (about 20-30 seconds).
      </p>
      {error ? <p className="token-identity-error">{error}</p> : null}
      <div className="post-unfreeze-grid">
        <div className="option">
          <strong>1 · Activate an ID up to a slot</strong>
          <label>ID <input value={bdId} onChange={(event) => setBdId(event.target.value)} placeholder="BD-10" /></label>
          <label>Up to slot
            <select value={upToSlot} onChange={(event) => setUpToSlot(event.target.value)} aria-label="Up to slot">
              {ACTIVATION_SLOTS.map((option) => <option key={option} value={String(option)}>{slotTitle(option)}{activationHint(option)}</option>)}
            </select>
          </label>
          <button className="button secondary small" type="button" disabled={busy !== null || !valid(bdId)} onClick={() => void run<ActivateResult>('Activation', 'test-slot/activate', { bdId: bdId.trim().toUpperCase(), upToSlot: Number(upToSlot) }, setActivated)}>{busy === 'Activation' ? 'Activating on-chain…' : 'Activate slots'}</button>
          {activated ? <p className="empty-copy">{activated.bdId}{activated.entrySlot ? ` (entered at Slot ${slotLabel(activated.entrySlot)})` : ''}: highest slot {slotLabel(activated.highestSlotBefore)} → {slotLabel(activated.highestSlotAfter)} · {tx(activated.txHash)}</p> : null}
        </div>
        <div className="option">
          <strong>2 · Deliver paid seat(s) into a slot</strong>
          <label>Matrix owner ID <input value={ownerBdId} onChange={(event) => setOwnerBdId(event.target.value)} placeholder="BD-10" /></label>
          <label>Slot
            <select value={slot} onChange={(event) => setSlot(event.target.value)} aria-label="Seat slot">
              {SEAT_SLOTS.map((option) => <option key={option} value={String(option)}>{slotTitle(option)} · {btcb(seatValueAtomic(option, matrix))}</option>)}
            </select>
          </label>
          <label>Occupant ID (who takes the seat) <input value={occupantBdId} onChange={(event) => setOccupantBdId(event.target.value)} placeholder="BD-11" /></label>
          <label>Number of seats (1-12) <input value={count} onChange={(event) => setCount(event.target.value)} /></label>
          <p className="empty-copy" data-testid="slot-test-seat-value">
            Seat value {btcb(seatValue)} ({valueSource}){seatCount >= 1 && seatCount <= 12 && Number.isInteger(seatCount) ? ` · ${seatCount} seat(s) = ${btcb(seatValue * BigInt(seatCount))}` : ''}.
          </p>
          <p className="empty-copy">Delivering into ½ or ¼ of an ID that entered at Slot 1 (or into ¼ of a Half-era ID) uses its virtual sub-slot: the first seat opens it with no payment by the owner, it always follows the cycle-2+ table and it never changes the ID's real slots.</p>
          <button className="button primary small" type="button" disabled={busy !== null || !valid(ownerBdId) || !valid(occupantBdId) || !(Number.isInteger(seatCount) && seatCount >= 1 && seatCount <= 12)} onClick={() => void deliverSeats()}>{busy === 'Seat delivery' ? `Delivering on-chain… ${progress ?? ''}` : 'Deliver seat(s)'}</button>
        </div>
      </div>
      {seated ? (
        <>
          <p className="empty-copy">{seated.ownerBdId} · {slotTitle(seated.slot, seated.virtual === true)}: {seated.seats.length} seat(s) of {formatToken(atomicToDecimal(seated.seatValueAtomic))} each filled by {seated.occupantBdId}{seated.virtualOpened ? ' · this delivery opened the virtual sub-slot' : ''}{seated.mintedAtomic !== '0' ? ` · ${formatToken(atomicToDecimal(seated.mintedAtomic))} mock BTCB minted for the test` : ''}. Next open seat: cycle {seated.nextCycle}, P{seated.nextPosition}. Next slot opened: {seated.virtual ? 'n/a (virtual slot)' : seated.nextSlotOpened ? 'yes' : 'no'}. Open the owner ID in Member explorer to see every seat, routing decision and payout.</p>
          <div className="pt-table-wrap"><table className="pt-table"><thead><tr><th>#</th><th>Cycle</th><th>Position</th><th>Tx</th></tr></thead><tbody>
            {seated.seats.map((seat, index) => <tr key={index}><td>{index + 1}</td><td>{seat.cycle}</td><td>P{seat.position}</td><td>{tx(seat.txHash)}</td></tr>)}
          </tbody></table></div>
        </>
      ) : null}
    </section>
  );
}
